import { mergeMap, take, throttleTime } from 'rxjs/operators';
import { EventEmitter, Inject, Injectable, NgZone, OnDestroy, Output } from '@angular/core';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { MaskActionComponent } from './components/mask-action/mask-action.component';
import { AuthService } from '@app/services/auth.service';
import { LogoffType } from '@app/origami-api/constants/logoff-type';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { from, fromEvent, Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { GlobalEventService, UserActivityMessages } from '@app/services/global-event.service';
import { Router } from '@angular/router';
import { LocalStorageKeys, LocalStorageService } from '@app/services/local-storage.service';

@Injectable()
export class InactivityMonitorService implements OnDestroy {
  private monitor;
  private monitorWorker: Worker;
  private popUpRef: PopupRef;
  private initialLogoutDelay: number;
  private logoutDelay: number;
  private initialTimeToIdle: number;
  private timeToIdle: number;
  private confirmationText: string;
  private isWatching: boolean;
  private readonly TIME_TO_IDLE = 'ti-timeToIdle';
  private readonly TAB_DEACTIVATION = 'ti-tabDeactivationTimestamp';
  private readonly strings = tiStrings.session.inactivityMonitor;
  private subs: Subscription[] = [];
  private activityListenerTimeout;
  @Output() timeToIdleChange = new EventEmitter<number>();

  private tabInactivationTimestamp: number;

  private readonly eventsToListen: string[] = ['mousemove', 'keydown'];

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly authService: AuthService,
    private readonly popupService: PopupService,
    private readonly globalEventService: GlobalEventService,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
  ) {
    // we need this logic to avoid effects of inactive tab throttling
    document.addEventListener('visibilitychange', () => {
      const now = new Date().getTime();

      if (document.visibilityState !== 'visible') {
        this.tabInactivationTimestamp = now;
        localStorage.setItem(this.TAB_DEACTIVATION, now + '');
        // Tab became inactive - switch to Web Worker if we have an active timer
        if (this.monitor) {
          this.switchToWorker();
        }
      }

      // need this to allow previous active tab to write things to localStorage
      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          // let's check if another TI tab was deactivated right now
          const cameFromAnotherTiTab = Math.abs(+localStorage.getItem(this.TAB_DEACTIVATION) - now) < 200;

          //console.log(localStorage.getItem(this.TAB_DEACTIVATION), +localStorage.getItem(this.TAB_DEACTIVATION) - now, cameFromAnotherTiTab);

          if (cameFromAnotherTiTab) {
            return; // no need to do anything
          }

          // Tab became active again - switch back to setInterval if we have an active worker
          if (this.monitorWorker) {
            this.switchToSetInterval();
          }

          if (now >= this.tabInactivationTimestamp + this.initialLogoutDelay * 1000 + this.initialTimeToIdle) {
            this.countdown(0);
          } else if (now >= this.tabInactivationTimestamp + this.initialTimeToIdle) {
            this.countdown(Math.floor((now - (this.tabInactivationTimestamp + this.initialTimeToIdle)) / 1000));
          }
        }
      }, 100);
    });
  }

  ngOnDestroy() {
    this.stop();
    this.removeActivityListener();
  }

  stop() {
    this.isWatching = false;
    clearInterval(this.monitor);
    if (this.monitorWorker) {
      this.monitorWorker.terminate();
      this.monitorWorker.onmessage = null;
      this.monitorWorker = null;
    }
    this.closePopup();
  }

  start(logoutDelaySec: number, timeToIdleMsec: number, confirmationText: string) {
    if (!this.timeToIdle) {
      // 1st run
      this.initialTimeToIdle = timeToIdleMsec;
      this.setTimeToIdle(new Date().getTime() + timeToIdleMsec);

      this.initialLogoutDelay = logoutDelaySec;

      const oldUrl = this.localStorageService.getItem(LocalStorageKeys.ti_url_before_expiration);
      this.localStorageService.removeItem(LocalStorageKeys.ti_url_before_expiration);

      let valid = true;
      const queryParams = this.router?.getCurrentNavigation()?.finalUrl?.queryParams || {};
      for (const key in queryParams) {
        if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
          const element = queryParams[key];

          if (element !== '') {
            valid = false;
          }
        }
      }

      const finalUrl =
        this.router?.getCurrentNavigation()?.finalUrl?.toString() || this.router?.routerState?.snapshot?.url || '';
      const isSpecialUrl =
        finalUrl.includes('document-viewer') ||
        finalUrl.includes('ti/domain/password-management') ||
        finalUrl.includes('auth/registration') ||
        finalUrl.includes('auth/password-reset') ||
        finalUrl.includes('auth/session-expired') ||
        finalUrl.includes('auth/global-directory'); // TODO: probably here we need more items to check

      const lastUserId = +this.localStorageService.getItem(LocalStorageKeys.ti_last_user_id);
      const currentUserId = +this.authService.authInfo?.info?.UserId;

      // Conflicts with TRL-16623
      /*
      if (!isSpecialUrl && lastUserId !== currentUserId) {
        this.router.navigateByUrl('/');
      }
      */

      this.localStorageService.setItem(LocalStorageKeys.ti_last_user_id, currentUserId + '');

      /*
      if (oldUrl && valid && this.router.url === '/' && !Object.keys(queryParams || {}).length) {
        this.router.navigateByUrl(oldUrl);
      }
      */

      if (!window.location.hash && oldUrl) {
        this.router.navigateByUrl(oldUrl);
      }
    }

    this.stop();
    this.listenForActivity();
    this.isWatching = true;
    this.logoutDelay = logoutDelaySec;
    this.timeToIdle = timeToIdleMsec;
    this.confirmationText = confirmationText;

    this.switchToSetInterval();
  }

  private switchToSetInterval() {
    if (this.monitorWorker) {
      this.monitorWorker.terminate();
      this.monitorWorker.onmessage = null;
      this.monitorWorker = null;
    }

    clearInterval(this.monitor);

    this.monitor = setInterval(() => {
      const tti = this.getTimeToIdle();
      const ct = new Date().getTime();
      //fetch(`http://localhost:8080/?timeToIdle=${tti}&currentTime=${ct}`);

      if (ct >= tti) {
        this.countdown(this.logoutDelay);
      }

      this.timeToIdleChange.emit(tti - ct);
    }, 1000);
  }

  private switchToWorker() {
    clearInterval(this.monitor);

    // Create a Web Worker from a Blob to avoid needing a separate file
    // Worker runs 1000ms interval consistently to maintain accurate timeout tracking
    const workerCode = `
      let interval;

      self.onmessage = function(event) {
        const { action, timeToIdle, logoutDelaySec } = event.data;

        if (action === 'start') {
          if (interval) clearInterval(interval);

          interval = setInterval(() => {
            const currentTime = new Date().getTime();
            const tti = timeToIdle;

            // Send tick to main thread
            self.postMessage({
              type: 'tick',
              currentTime: currentTime,
              timeToIdle: tti
            });
          }, 1000);
        } else if (action === 'stop') {
          if (interval) clearInterval(interval);
        }
      };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);

    this.monitorWorker = new Worker(workerUrl);
    this.monitorWorker.onmessage = (event) => {
      if (event.data.type === 'tick') {
        const tti = this.getTimeToIdle();
        const ct = event.data.currentTime;

        if (ct >= tti) {
          this.countdown(this.logoutDelay);
        }

        this.timeToIdleChange.emit(tti - ct);
      }
    };

    // Start the worker
    this.monitorWorker.postMessage({
      action: 'start',
      timeToIdle: this.getTimeToIdle(),
      logoutDelaySec: this.logoutDelay,
    });

    // Cleanup blob URL
    URL.revokeObjectURL(workerUrl);
  }

  showMask() {
    if (this.logoutDelay) {
      this.countdown(this.logoutDelay);
    }
  }

  public onActivity(event?) {
    if (!this.isWatching) {
      this.closePopup();
    }

    if (this.monitorWorker) {
      return;
    }

    this.stop();

    this.activityListenerTimeout =
      this.activityListenerTimeout ||
      setTimeout(() => {
        this.activityListenerTimeout = null;
        this.setTimeToIdle(new Date().getTime() + this.timeToIdle);
        this.start(this.logoutDelay, this.timeToIdle, this.confirmationText);
      }, 5000);
  }

  private countdown(logoutDelay: number) {
    this.stop();

    if (logoutDelay <= 0) {
      this.removeActivityListener();
    }

    this.popUpRef = this.popupService.open({
      animate: false,
      content: MaskActionComponent,
    });

    const instance: MaskActionComponent = this.popUpRef.content.instance;
    instance.secondsToAction = logoutDelay;
    instance.mainMessage = this.confirmationText;
    instance.optionalMessage = (seconds) => tiStringsFormat(this.strings.secondMessagePattern, `${seconds}`);
    instance.done.pipe(take(1)).subscribe(() => {
      this.removeActivityListener();
      this.localStorageService.setItem(LocalStorageKeys.ti_url_before_expiration, this.router.url);
      this.authService.logout(LogoffType.LogOffByIdle);
      this.closePopup();
    });
    instance.start();
  }

  private getTimeToIdle(): number {
    const t = localStorage.getItem(this.TIME_TO_IDLE);
    if (t !== null) {
      return +t;
    }

    const tt = new Date().getTime() + this.timeToIdle;
    this.setTimeToIdle(tt);
    return tt;
  }

  private closePopup() {
    if (this.popUpRef) {
      this.popUpRef.content.instance.done.unsubscribe();
      this.popUpRef.close();
      this.popUpRef = null;
    }
  }

  private removeActivityListener() {
    this.subs.forEach((s) => s && s.unsubscribe());
  }

  private listenForActivity() {
    this.removeActivityListener();

    this.zone.runOutsideAngular(() => {
      this.subs.push(
        from(this.eventsToListen)
          .pipe(
            mergeMap((event) => fromEvent(this.document, event)),
            throttleTime(1000),
          )
          .subscribe((event) => this.onActivity(event)),

        this.globalEventService
          .on(UserActivityMessages.bulkUploadActivity)
          .subscribe((event) => this.onActivity(event)),

        this.globalEventService.on(UserActivityMessages.iframeActivity).subscribe((event) => this.onActivity(event)),
      );
    });
  }

  private setTimeToIdle(t: number) {
    localStorage.setItem(this.TIME_TO_IDLE, t.toString());
  }
}
